import React, { useEffect } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import { ScrollView } from 'react-native-web-hover';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { documentService } from '../../../services/central-api/document';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../../../components/MyAppText';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import DocumentCategoryDropdown from '../../../components/Filters/Documents/DocumentCategoryDropdown';
import FileUploader from '../../../components/FileUploader';
import { useForm, useWatch } from 'react-hook-form';

const acceptedMimeTypes = [
    'application/pdf',
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'video/mp4',
    'image/jpeg',
    'image/png',
];

interface DocumentForm {
    actingBodyId: number;
    actingBodyUnitId: number;
    documentCategoryId: number;
    title: string;
    file: File | undefined;
    fileName: string;
}

export default function DocumentModal({ onClose, documentId, isModalVisible }: { onClose: () => void; documentId?: number; isModalVisible: boolean; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const isEdit = !!documentId;

    const defaultEmptyValues: DocumentForm = {
        actingBodyId: 0,
        actingBodyUnitId: 0,
        documentCategoryId: 0,
        title: '',
        file: undefined,
        fileName: ''
    };

    const form = useForm<DocumentForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const actingBodyId = useWatch({ control: form.control, name: 'actingBodyId' });
    const actingBodyUnitId = useWatch({ control: form.control, name: 'actingBodyUnitId' });

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    async function getDocument(documentId: number) {
        try {
            const document = await documentService.getDocumentById(documentId);
            form.reset({
                title: document.title,
                documentCategoryId: document.documentCategory.id,
                actingBodyUnitId: document.actingBodyUnitId,
                actingBodyId: document.actingBodyUnit.actingBody.id,
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            clearValues();
            onClose();
        }
    }

    useEffect(() => {
        if (isModalVisible && documentId) {
            getDocument(documentId);
        }
    }, [documentId, isModalVisible]);

    useEffect(() => {
        if (isEdit) {
            return;
        }
        form.setValue('actingBodyUnitId', 0);
        form.setValue('documentCategoryId', 0);
    }, [actingBodyId]);

    useEffect(() => {
        if (isEdit) {
            return;
        }
        form.setValue('documentCategoryId', 0);
    }, [actingBodyUnitId]);

    function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) {
            return;
        }
        const selectedFile = e.target.files[0];
        form.setValue('file', selectedFile);
        form.setValue('fileName', selectedFile.name);
    }

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (!data.file) {
                return;
            }

            if (isEdit) {
                await documentService.updateDocument(documentId, data.file);
            } else {
                await documentService.createDocument({
                    title: data.title,
                    documentCategoryId: data.documentCategoryId,
                    actingBodyUnitId: data.actingBodyUnitId,
                    file: data.file,
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '30%' }]}>
                        <ScrollView style={{ flex: 1, minWidth: 300, minHeight: 350 }} contentContainerStyle={{ flexDirection: 'column', gap: 24 }}>
                            <View style={{ flexDirection: 'column', gap: 8 }}>
                                <MyAppText style={styles.header}>{translate(documentId ? 'updateDocument' : 'registerDocument')}</MyAppText>
                                {
                                    documentId &&
                                    <MyAppText style={styles.subheader}>{translate('updatedDocumentDescription')}</MyAppText>
                                }
                            </View>
                            <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 12, zIndex: 4 }}>
                                <View style={{ flex: 1, zIndex: 4 }}>
                                    <ActingBodiesDropdown
                                        includeAllOption={false}
                                        disabled={isEdit}
                                        required
                                        form={form}
                                        fieldName='actingBodyId'
                                        zIndex={4}
                                        semiBoldLabel={true}
                                        labelFontSize={14}
                                    />
                                </View>

                                <View style={{ flex: 1, zIndex: 3 }}>
                                    <ActingBodiesUnitsDropdown
                                        actingBodyId={actingBodyId}
                                        form={form}
                                        fieldName='actingBodyUnitId'
                                        disabled={isEdit}
                                        required
                                        zIndex={3}
                                        semiBoldLabel={true}
                                        labelFontSize={14}
                                    />
                                </View>
                            </View>

                            <FormInput
                                label={translate('title')}
                                placeholder={translate('title')}
                                disabled={isEdit}
                                required
                                form={form}
                                fieldName='title'
                                semiBoldLabel={true}
                            />

                            <DocumentCategoryDropdown
                                actingBodyUnitId={actingBodyUnitId}
                                disabled={isEdit}
                                required
                                form={form}
                                fieldName='documentCategoryId'
                                zIndex={2}
                                semiBoldLabel={true}
                                labelFontSize={14}
                            />

                            <View style={{ gap: 8 }}>
                                <View style={{ flexDirection: 'row', gap: 4 }}>
                                    <MyAppText style={styles.inputTitle}>{translate('documentFile')}</MyAppText>
                                    <MyAppText style={styles.filterTextRequired}>(*)</MyAppText>
                                </View>
                                <FileUploader
                                    text={form.watch('fileName') || translate('attach')}
                                    icon={faUpload}
                                    accept={acceptedMimeTypes.join(',')}
                                    onChange={onFileChange}
                                />
                            </View>
                        </ScrollView>

                        <FormActions
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!form.formState.isValid || !form.watch('file')}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        padding: 32,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    inputTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: '600',
    },
    filterTextRequired: {
        color: theme.colors.errors['300'],
        fontWeight: '600'
    },
}));
